/* eslint-disable @typescript-eslint/no-explicit-any */
import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import { PageOptions } from '../../../@types/FiltersTypes';
import { api } from '../../../lib';
import { withPendingEditedPrice } from '../../../store/priceManagerStore';
import { AlterarPrecosSchema } from './Components';
import { PriceManagerDatapointsModel } from './hooks/usePriceManagerDatapointsModel';
import { SAVED_FILTERS_PROPS, getValidData } from './utils';

export type GetSavedFiltersDataProps = {
  search?: string;
  screen: PageOptions;
};

export function getSavedFilters({ screen = 'PRICE_MANAGER', ...data }: GetSavedFiltersDataProps): Promise<SAVED_FILTERS_PROPS> {
  return trackPromise(
    // @ts-ignore
    api({
      url: '/all-products/user-filters',
      method: 'GET',
      expectToken: true,
      params: getValidData({ screen, ...data }),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp.data),
    'saved-filters',
  );
}

export type SavedFiltersPayload = Partial<Record<string, unknown>>;

export type PostSavedFitersDataProps<TFilters = PriceManagerDatapointsModel> = {
  id?: string;
  name: string;
  screen: PageOptions;
  filters: TFilters;
};

export async function postSavedFilter<TFilters = PriceManagerDatapointsModel>({ screen = 'PRICE_MANAGER', ...data }: PostSavedFitersDataProps<TFilters>) {
  return trackPromise(
    // @ts-ignore
    api({
      url: '/all-products/user-filters',
      method: 'POST',
      expectToken: true,
      data: getValidData({
        screen,
        ...data,
      }),
    }).then(resp => resp),
    'saved-filter',
  );
}

export async function putSavedFilter<TFilters = PriceManagerDatapointsModel>(data: PostSavedFitersDataProps<TFilters>) {
  return trackPromise(
    // @ts-ignore
    api({
      url: '/all-products/user-filters',
      method: 'PUT',
      expectToken: true,
      data,
    }).then(resp => resp),
    'saved-filter',
  );
}

export async function deleteSavedFilter(id: string) {
  return trackPromise(
    // @ts-ignore
    api({
      url: `/all-products/user-filters/${id}`,
      method: 'DELETE',
      expectToken: true,
    }).then(resp => resp),
    'saved-filter',
  );
}

export type IGetCompetitorsInfo = {
  productId: string;
  storeId: string;
  productFamilyId: string | null;
  competitorStoreId?: string[];
  clusterId?: string;
};

export async function getCompetitorsInfo(
  { productId = '', storeId = '1', productFamilyId = '', competitorStoreId = [], clusterId }: IGetCompetitorsInfo,
  loadingArea?: string,
) {
  return trackPromise(
    api({
      url: `/competitor/prices/${productId}`,
      method: 'GET',
      expectToken: true,
      params: getValidData({
        storeId,
        productFamilyId,
        competitorStoreId,
        clusterId,
      }),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp.data),
    loadingArea,
  );
}

export type ChangeMassicePrice = AlterarPrecosSchema & {
  products: Record<'selectedIds' | 'excludedIds', string[]>;
  selectedAll: boolean;
  filters: PriceManagerDatapointsModel;
};

export async function changeMassivePrice({ filters: { sort, page, size, ...filters }, ...data }: ChangeMassicePrice): Promise<{ productsFailed: number }> {
  return withPendingEditedPrice(() =>
    trackPromise(
      api({
        url: '/all-products/update/price/bulk',
        method: 'POST',
        expectToken: true,
        params: {
          sort,
          page,
          size,
        },
        paramsSerializer(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
        data: {
          ...data,
          filters,
        },
      }).then(res => res.data),
      'ipa/gerenciador/alterar-precos',
    ),
  );
}

export type DownloadSelectedDatapoints = {
  selectedIds: string[];
  excludedIds: string[];
  selectedAll: boolean;
  datapointExhibitionType: 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY';
  filters: any;
};

export async function downloadSelectedDatapoints({ filters, selectedIds, excludedIds, selectedAll, datapointExhibitionType }: DownloadSelectedDatapoints) {
  return trackPromise(
    api({
      url: '/all-products/manager/download',
      method: 'POST',
      expectToken: true,
      data: {
        selectedIds,
        excludedIds,
        selectedAll,
        datapointExhibitionType,
        filters,
      },
      responseType: 'arraybuffer',
    }),
    'ipa/gerenciador/download-selected',
  );
}

export const generateDownloadFileName = (): string => {
  const today = new Date();
  const day = today.getDate().toString().padStart(2, '0');
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear();
  return `${day}-${month}-${year}-ipa_gerenciador.csv`;
};

export const downloadFile = (data: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

type PriceEventArchitectureContext = {
  architectureId?: string | null;
  architectureRefProduct?: string | null;
};

type PriceEventDetails =
  | {
      eventType: 'ERP';
      price?: number | null;
    }
  | {
      eventType: 'RULE';
      price?: number;
      trigger?: string;
      author: {
        name: string;
        id: string;
      };
    }
  | {
      eventType: 'MIN_VARIATION';
      brokenPrice: boolean;
    }
  | {
      eventType: 'RANGE';
      price?: number;
    }
  | {
      eventType: 'LIMIT';
      brokenPrice: boolean;
      adjustedPriceValue?: number | null;
      author: {
        name: string;
        id: string;
      };
    }
  | {
      eventType: 'PRICE_LIST';
      brokenPrice: boolean;
    }
  | {
      eventType: 'PRICE';
      hour: string;
      price?: number;
    }
  | {
      eventType: 'EDITED_PRICE';
      hour: string;
      price?: number;
      trigger?: string;
      author: {
        name: string;
      };
    }
  | {
      eventType: 'APPLIED_PRICE';
      hour: string;
      price?: number;
      author: {
        name: string;
      };
    }
  | {
      eventType: 'EDITED_PRICE_WHOLESALE';
      hour: string;
      price?: number;
      author: {
        name: string;
      };
    }
  | {
      eventType: 'EDITED_PRICE_OFFER';
      price?: number;
      hour: string;

      author: {
        name: string;
      };
    };

export type PriceEvent = PriceEventArchitectureContext & PriceEventDetails;

export type PriceEventHistory = {
  date: string;
  occurrences: PriceEvent[];
}[];

export async function getPriceEventHistory(productsToBePricedId: string): Promise<PriceEventHistory> {
  const res = await trackPromise(
    api.get<PriceEventHistory>('/all-products/datapoint-event-history', {
      params: {
        productsToBePricedId,
      },
      expectToken: true,
    }),
    'price-composition',
  );

  return res.data;
}
